import { useEffect, useId, useMemo, useRef, useState } from "react";
import { FiCheck, FiChevronDown } from "react-icons/fi";
import styles from "./Select.module.css";

/**
 * Custom single-value dropdown that replaces native <select> app-wide.
 *
 * Native selects draw their closed control AND their open list with the OS widget — Safari and
 * Chrome render visibly different controls for the same CSS (the reason this exists), and the
 * open list can't be styled at all. This renders both parts itself, identically everywhere.
 *
 * Drop-in by design: `onChange` receives `{ target: { name, value } }`, so call sites keep the
 * exact handlers they used with a native select — including shared form handlers that read
 * `e.target.name`. Values are exposed as STRINGS, again matching the native element, so existing
 * `value === ""` / `Number(value)` logic is unaffected.
 *
 * @param options  [{ value, label, disabled? }] — include an empty-value row where the native
 *                 select had a selectable placeholder option.
 * @param placeholder  Shown (greyed) when the current value matches no option.
 * @param className    Applied to the trigger, so existing width/border styles carry over; when
 *                     omitted the trigger falls back to the app's default input look.
 * @param inline       Size to the content (like an unstyled native select) instead of filling the
 *                     container. Needed inside `flex-wrap: wrap` rows, where the default
 *                     full-width root would claim a whole line of its own; in non-wrapping flex
 *                     rows and modal forms the default is right.
 */
const Select = ({
    value,
    onChange,
    options = [],
    placeholder = "",
    name,
    id,
    disabled = false,
    className = "",
    inline = false,
    ...rest
}) => {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [openUp, setOpenUp] = useState(false);
    const rootRef = useRef(null);
    const listRef = useRef(null);
    const typeBuffer = useRef({ text: "", at: 0 });
    const listId = useId();

    const normalised = useMemo(
        () => options.map((o) => ({ ...o, value: o.value == null ? "" : String(o.value) })),
        [options]
    );
    const current = String(value == null ? "" : value);
    const selectedIndex = normalised.findIndex((o) => o.value === current);
    const selected = selectedIndex >= 0 ? normalised[selectedIndex] : null;

    const emit = (nextValue) => {
        onChange?.({ target: { name, value: nextValue } });
    };

    const close = () => {
        setOpen(false);
        setHighlight(-1);
    };

    const openList = () => {
        if (disabled) return;
        // Open upward when the space below can't fit the list — inside scrollable modal bodies
        // the downward popup would otherwise be clipped by the container's overflow.
        const rect = rootRef.current?.getBoundingClientRect();
        if (rect) setOpenUp(window.innerHeight - rect.bottom < 280 && rect.top > 280);
        setHighlight(selectedIndex >= 0 ? selectedIndex : 0);
        setOpen(true);
    };

    const pick = (index) => {
        const option = normalised[index];
        if (!option || option.disabled) return;
        emit(option.value);
        close();
    };

    // Close on any press outside — pointerdown, not click, so it also closes when the outside
    // press starts a drag or lands on something that stops propagation.
    useEffect(() => {
        if (!open) return;
        const onOutside = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) close();
        };
        document.addEventListener("pointerdown", onOutside);
        return () => document.removeEventListener("pointerdown", onOutside);
    }, [open]);

    // Keep the highlighted row visible while arrowing through a long list.
    useEffect(() => {
        if (!open || highlight < 0) return;
        listRef.current
            ?.querySelector(`[data-index="${highlight}"]`)
            ?.scrollIntoView({ block: "nearest" });
    }, [open, highlight]);

    const moveHighlight = (from, step) => {
        // Skip disabled rows in the travel direction; stop at the ends.
        let next = from;
        do {
            next += step;
        } while (normalised[next]?.disabled);
        if (next >= 0 && next < normalised.length) setHighlight(next);
    };

    const typeToJump = (key) => {
        const now = Date.now();
        const fresh = now - typeBuffer.current.at > 700;
        typeBuffer.current = {
            text: (fresh ? "" : typeBuffer.current.text) + key.toLowerCase(),
            at: now,
        };
        const needle = typeBuffer.current.text;
        const match = normalised.findIndex(
            (o) => !o.disabled && String(o.label).toLowerCase().startsWith(needle)
        );
        if (match >= 0) {
            if (open) setHighlight(match);
            else emit(normalised[match].value);
        }
    };

    const onKeyDown = (e) => {
        if (disabled) return;
        switch (e.key) {
            case "ArrowDown":
                e.preventDefault();
                open ? moveHighlight(highlight, 1) : openList();
                break;
            case "ArrowUp":
                e.preventDefault();
                open ? moveHighlight(highlight, -1) : openList();
                break;
            case "Home":
                if (open) { e.preventDefault(); moveHighlight(-1, 1); }
                break;
            case "End":
                if (open) { e.preventDefault(); moveHighlight(normalised.length, -1); }
                break;
            case "Enter":
            case " ":
                e.preventDefault();
                open ? pick(highlight) : openList();
                break;
            case "Escape":
                if (open) { e.preventDefault(); close(); }
                break;
            case "Tab":
                close();
                break;
            default:
                if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) typeToJump(e.key);
        }
    };

    return (
        <div className={`${styles.root} ${inline ? styles.rootInline : ""}`} ref={rootRef}>
            <button
                type="button"
                id={id}
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls={listId}
                aria-activedescendant={open && highlight >= 0 ? `${listId}-${highlight}` : undefined}
                disabled={disabled}
                className={`${styles.trigger} ${className || styles.triggerDefault}`}
                onClick={() => (open ? close() : openList())}
                onKeyDown={onKeyDown}
                {...rest}
            >
                <span className={`${styles.valueText} ${selected ? "" : styles.placeholder}`}>
                    {selected ? selected.label : placeholder}
                </span>
                <FiChevronDown className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`} aria-hidden />
            </button>

            {open && (
                <ul
                    id={listId}
                    ref={listRef}
                    role="listbox"
                    className={`${styles.list} ${openUp ? styles.listUp : ""}`}
                >
                    {normalised.length === 0 && <li className={styles.empty}>—</li>}
                    {normalised.map((option, index) => (
                        <li
                            key={`${option.value}-${index}`}
                            id={`${listId}-${index}`}
                            data-index={index}
                            role="option"
                            aria-selected={option.value === current}
                            aria-disabled={option.disabled || undefined}
                            className={[
                                styles.option,
                                index === highlight ? styles.optionActive : "",
                                option.value === current ? styles.optionSelected : "",
                                option.disabled ? styles.optionDisabled : "",
                            ].join(" ")}
                            onPointerEnter={() => !option.disabled && setHighlight(index)}
                            onClick={() => pick(index)}
                        >
                            <span className={styles.optionLabel}>{option.label}</span>
                            {option.value === current && <FiCheck className={styles.checkIcon} aria-hidden />}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

export default Select;
